// Setup → site → Guard tours (api-phase4.md "Guard tours", prototype renderSetup site 'ai' "Guard tours" table).
// Admins create, edit and switch tours off; supervisors read. On a schedule the vision workers walk the tour's cameras
// looking for people or vehicles; each finding at or above the minimum confidence becomes an ordinary alarm
// (source guard_tour) that correlation, the agent and the operators work like any other. The runs view shows each
// walk: status, cameras checked / unavailable, and the exceptions with a link to the alarm's event.

import { useState } from 'react';
import { api, errorText, type Camera, type GuardTour, type GuardTourBody, type GuardTourRun, type GuardTourWindow } from '../../api.ts';
import { AlarmDetailPanel } from '../../components/AlarmDetailPanel.tsx';
import { Icon } from '../../components/Icon.tsx';
import { Empty, ErrorBox, InlineError, Loading, Pill, StatusPill } from '../../components/ui.tsx';
import { pct } from '../../console/ai.ts';
import { camShort, human, ms } from '../../console/util.ts';
import { useLoad } from '../../hooks.ts';
import { href, navigate } from '../../router.ts';
import { fmtDateTime, relAge, tzAbbr } from '../../time.ts';
import { DAYS, Toggle } from './common.tsx';

const FOCUS = ['person', 'vehicle'] as const;

function camNames(ids: string[], cams: Camera[]): string {
  const short = ids.map((id) => camShort(cams.find((c) => c.id === id)?.name ?? id));
  return short.join(', ') || '—';
}

function windowText(w: GuardTourWindow): string {
  const days = w.days?.length ? (w.days.length === 7 ? 'every day' : w.days.map((d) => human(d)).join(', ')) : 'every day';
  return `${days} ${w.start}–${w.end}`;
}

function scheduleText(t: Pick<GuardTour, 'everyMinutes' | 'windows'>): string {
  const every = t.everyMinutes % 60 === 0 ? `Every ${t.everyMinutes / 60} h` : `Every ${t.everyMinutes} min`;
  return `${every} · ${t.windows.length ? t.windows.map(windowText).join('; ') : 'whenever the site is armed'}`;
}

/* ---------- editor ---------- */

interface Draft {
  name: string;
  cameraIds: string[];
  everyMinutes: number;
  windows: GuardTourWindow[];
  focus: ('person' | 'vehicle')[];
  minConfidence: number;
  active: boolean;
}

function draftOf(t: GuardTour | null): Draft {
  return t
    ? { name: t.name, cameraIds: t.cameraIds, everyMinutes: t.everyMinutes, windows: t.windows, focus: t.focus, minConfidence: t.minConfidence, active: t.active }
    : { name: '', cameraIds: [], everyMinutes: 30, windows: [], focus: ['person', 'vehicle'], minConfidence: 0.6, active: true };
}

function TourForm({ siteId, tour, cams, onDone, onCancel }: { siteId: string; tour: GuardTour | null; cams: Camera[]; onDone: (t: GuardTour) => void; onCancel: () => void }) {
  const [d, setD] = useState<Draft>(() => draftOf(tour));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((o) => ({ ...o, [k]: v }));
  const setWin = (i: number, p: Partial<GuardTourWindow>) => setD((o) => ({ ...o, windows: o.windows.map((w, j) => (j === i ? { ...w, ...p } : w)) }));
  const toggleCam = (id: string) => setD((o) => ({ ...o, cameraIds: o.cameraIds.includes(id) ? o.cameraIds.filter((x) => x !== id) : [...o.cameraIds, id] }));
  const toggleFocus = (f: 'person' | 'vehicle') => setD((o) => ({ ...o, focus: o.focus.includes(f) ? o.focus.filter((x) => x !== f) : [...o.focus, f] }));
  const everyBad = !Number.isInteger(d.everyMinutes) || d.everyMinutes < 5 || d.everyMinutes > 1440;
  const winBad = d.windows.some((w) => !/^\d\d:\d\d$/.test(w.start) || !/^\d\d:\d\d$/.test(w.end));
  const valid = d.name.trim() && d.cameraIds.length && d.focus.length && !everyBad && !winBad;
  const save = async () => {
    setTried(true);
    if (!valid) return;
    setBusy(true);
    setErr(null);
    const body: GuardTourBody = {
      name: d.name.trim(),
      cameraIds: d.cameraIds,
      everyMinutes: d.everyMinutes,
      windows: d.windows.map((w) => ({ start: w.start, end: w.end, ...(w.days?.length ? { days: w.days } : {}) })),
      focus: d.focus,
      minConfidence: Math.round(d.minConfidence * 100) / 100,
      active: d.active,
    };
    try {
      onDone(tour ? await api.patchGuardTour(siteId, tour.id, body) : await api.createGuardTour(siteId, body));
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card2 gtform">
      <div className="card-h">
        <h3>
          <Icon name="route" />
          {tour ? `Edit ${tour.name}` : 'New guard tour'}
        </h3>
      </div>
      <div className="fgrid">
        <label className="field">
          <span className="lbl">Name</span>
          <input value={d.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Perimeter" maxLength={100} aria-invalid={tried && !d.name.trim()} autoFocus />
          {tried && !d.name.trim() && <span className="inline-err">Give the tour a name.</span>}
        </label>
        <label className="field">
          <span className="lbl">Every (minutes, 5–1440)</span>
          <input type="number" min={5} max={1440} step={5} value={d.everyMinutes} onChange={(e) => set('everyMinutes', Math.round(Number(e.target.value) || 0))} aria-invalid={everyBad} />
          {everyBad && <span className="inline-err">Use 5 to 1440 minutes.</span>}
        </label>
      </div>
      <div className="field">
        <span className="lbl">Cameras</span>
        <div className="chips" role="group" aria-label="Cameras">
          {cams.map((c) => {
            const on = d.cameraIds.includes(c.id);
            return (
              <button key={c.id} type="button" className={`chip aichip${on ? ' on' : ''}`} aria-pressed={on} onClick={() => toggleCam(c.id)}>
                {c.name}
              </button>
            );
          })}
          {cams.length === 0 && <span className="note">This site has no cameras in the replica.</span>}
        </div>
        {tried && !d.cameraIds.length && <span className="inline-err">Pick at least one camera.</span>}
      </div>
      <div className="fgrid">
        <div className="field">
          <span className="lbl">Looks for</span>
          <div className="chips" role="group" aria-label="Looks for">
            {FOCUS.map((f) => {
              const on = d.focus.includes(f);
              return (
                <button key={f} type="button" className={`daybtn skillbtn${on ? ' on' : ''}`} aria-pressed={on} onClick={() => toggleFocus(f)}>
                  {f === 'person' ? 'People' : 'Vehicles'}
                </button>
              );
            })}
          </div>
          {tried && !d.focus.length && <span className="inline-err">Pick people, vehicles, or both.</span>}
        </div>
        <label className="field">
          <span className="lbl">Minimum confidence · {pct(d.minConfidence)}</span>
          <input type="range" min={0.3} max={0.99} step={0.01} value={d.minConfidence} onChange={(e) => set('minConfidence', Number(e.target.value))} className="airange" />
          <span className="note">Detections below this are ignored. A finding becomes an alarm.</span>
        </label>
      </div>
      <div className="sec">
        <div className="lbl">
          <span>Windows (site time)</span>
          <button type="button" className="btn sm" onClick={() => set('windows', [...d.windows, { days: [], start: '22:00', end: '05:00' }])}>
            <Icon name="plus" />
            Add window
          </button>
        </div>
        {d.windows.length === 0 && <div className="note">No windows: it runs whenever the site is armed (contracted hours or a monitor override).</div>}
        {d.windows.map((w, i) => (
          <div key={i} className="editrow">
            <div className="days" role="group" aria-label="Days">
              {DAYS.map((day) => {
                const on = w.days?.includes(day) ?? false;
                return (
                  <button key={day} type="button" className={`daybtn${on ? ' on' : ''}`} aria-pressed={on} onClick={() => setWin(i, { days: on ? (w.days ?? []).filter((x) => x !== day) : [...(w.days ?? []), day] })}>
                    {day.slice(0, 2)}
                  </button>
                );
              })}
            </div>
            <input type="time" value={w.start} onChange={(e) => setWin(i, { start: e.target.value })} aria-label="From" className="compact" />
            <input type="time" value={w.end} onChange={(e) => setWin(i, { end: e.target.value })} aria-label="To" className="compact" />
            <span className="note">{w.days?.length ? '' : 'every day'}</span>
            <button type="button" className="btn icon sm" onClick={() => set('windows', d.windows.filter((_, j) => j !== i))} aria-label="Remove window">
              <Icon name="trash" />
            </button>
          </div>
        ))}
        {winBad && <span className="inline-err">Each window needs a start and an end.</span>}
        <div className="note">No days = every day. Windows can cross midnight (22:00–05:00).</div>
      </div>
      <Toggle checked={d.active} onChange={(v) => set('active', v)} label="Active" />
      <div className="savebar">
        <button type="button" className="btn pri" onClick={() => void save()} disabled={busy}>
          <Icon name="check" />
          {busy ? 'Saving…' : tour ? 'Save tour' : 'Create tour'}
        </button>
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <InlineError error={err} />
      </div>
    </div>
  );
}

/* ---------- runs ---------- */

/**
 * The event the finding's alarm joined. Alarms don't carry their event id, so look at the site's events around the
 * run and find the one with this alarm.
 */
async function findAlarmEvent(siteId: string, alarmId: string, at: string | null): Promise<string | null> {
  const t = ms(at) ?? Date.now();
  const list = await api.events({ siteId, state: 'all', limit: 100 });
  const near = list
    .filter((e) => (ms(e.firstAlarmAt) ?? 0) <= t + 60_000 && (ms(e.lastAlarmAt) ?? 0) >= t - 60_000)
    .sort((a, b) => Math.abs((ms(a.firstAlarmAt) ?? 0) - t) - Math.abs((ms(b.firstAlarmAt) ?? 0) - t))
    .slice(0, 6);
  for (const e of near) {
    const d = await api.event(e.id);
    if (d.alarms.some((a) => a.id === alarmId)) return d.id;
  }
  return null;
}

function ExceptionLink({ siteId, alarmId, at, onAlarm }: { siteId: string; alarmId: string; at: string | null; onAlarm: (id: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const open = async () => {
    setBusy(true);
    setErr(null);
    try {
      const id = await findAlarmEvent(siteId, alarmId, at);
      if (id) navigate(href.console(id));
      else setErr('No event found for this alarm (it may have been suppressed).');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="row-wrap nowrap">
      <button type="button" className="btn sm" onClick={() => void open()} disabled={busy}>
        <Icon name="eye" />
        {busy ? 'Finding…' : 'Event'}
      </button>
      <button type="button" className="btn sm" onClick={() => onAlarm(alarmId)}>
        <Icon name="bolt" />
        Alarm
      </button>
      {err && <span className="note warn-t">{err}</span>}
    </span>
  );
}

function Runs({ siteId, tour, cams, tz, onClose }: { siteId: string; tour: GuardTour; cams: Camera[]; tz: string; onClose: () => void }) {
  const runs = useLoad((sig) => api.guardTourRuns(siteId, tour.id, sig), [siteId, tour.id]);
  const [alarm, setAlarm] = useState<string | null>(null);
  return (
    <div className="card2 flush">
      <div className="card-h pad">
        <h3>
          <Icon name="log" />
          Runs · {tour.name}
        </h3>
        <span className="card-r row-wrap">
          <span className="note">last 50 · site time {tzAbbr(tz)}</span>
          <button type="button" className="btn sm" onClick={runs.reload}>
            <Icon name="sync" />
            Refresh
          </button>
          <button type="button" className="btn sm" onClick={onClose}>
            <Icon name="x" />
            Close
          </button>
        </span>
      </div>
      {runs.error && !runs.data ? (
        <ErrorBox error={runs.error} onRetry={runs.reload} title="Could not load the runs" />
      ) : !runs.data ? (
        <Loading label="Loading runs…" />
      ) : runs.data.length === 0 ? (
        <Empty>No runs yet. A tour runs while the site is live and armed, inside its windows.</Empty>
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th scope="col">Started</th>
                <th scope="col">Status</th>
                <th scope="col" className="num">
                  Checked
                </th>
                <th scope="col" className="num">
                  Unavailable
                </th>
                <th scope="col">Exceptions</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.map((r: GuardTourRun) => (
                <tr key={r.id}>
                  <td className="mono nowrap">{fmtDateTime(r.startedAt, tz)}</td>
                  <td>
                    <StatusPill status={r.status} />
                    {r.skipReason && <div className="note">{r.skipReason}</div>}
                  </td>
                  <td className="mono num">{r.camerasChecked ?? '—'}</td>
                  <td className={`mono num${r.camerasUnavailable ? ' warn-t' : ''}`}>{r.camerasUnavailable ?? '—'}</td>
                  <td>
                    {!r.exceptions?.length ? (
                      <span className="dim">none</span>
                    ) : (
                      <div className="gtexc">
                        {r.exceptions.map((x) => (
                          <div key={x.alarmId} className="row-wrap">
                            <span className="pill ai">
                              <Icon name="route" />
                              {human(x.label)} {pct(x.confidence)}
                            </span>
                            <span>{camNames([x.cameraId], cams)}</span>
                            <ExceptionLink siteId={siteId} alarmId={x.alarmId} at={r.finishedAt ?? r.startedAt} onAlarm={setAlarm} />
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {alarm && <AlarmDetailPanel id={alarm} onClose={() => setAlarm(null)} />}
    </div>
  );
}

/* ---------- the tab ---------- */

export function GuardToursTab({ siteId, canEdit }: { siteId: string; canEdit: boolean }) {
  const tours = useLoad((sig) => api.guardTours(siteId, sig), [siteId]);
  const site = useLoad((sig) => api.site(siteId, sig), [siteId]);
  const [edit, setEdit] = useState<GuardTour | 'new' | null>(null);
  const [runsOf, setRunsOf] = useState<GuardTour | null>(null);
  const [confirm, setConfirm] = useState<GuardTour | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const cams = (site.data?.cameras ?? []).filter((c) => c.active).sort((a, b) => (a.displayNumber ?? 999) - (b.displayNumber ?? 999) || a.name.localeCompare(b.name));
  const tz = site.data?.timeZone ?? 'UTC';
  const replace = (t: GuardTour) => tours.setData((l) => (l ? (l.some((x) => x.id === t.id) ? l.map((x) => (x.id === t.id ? { ...t, lastRun: t.lastRun ?? x.lastRun } : x)) : [...l, t]) : [t]));
  const setActive = async (t: GuardTour, active: boolean) => {
    setBusy(t.id);
    setErr(null);
    try {
      replace(active ? await api.patchGuardTour(siteId, t.id, { active: true }) : await api.disableGuardTour(siteId, t.id));
      setConfirm(null);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(null);
    }
  };
  if (tours.error && !tours.data) return <ErrorBox error={tours.error} onRetry={tours.reload} title="Could not load guard tours" />;
  if (!tours.data) return <Loading label="Loading guard tours…" />;
  const list = tours.data.slice().sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
  return (
    <div className="tabbody">
      <div className="banner">
        <Icon name="route" />
        <span>
          On a schedule, the vision workers walk these cameras looking for people or vehicles. Each finding becomes an alarm (labelled <b>guard tour</b>) that the AI agent and the operators work like any other. Tours
          run only while the site is live and armed.
        </span>
        {canEdit && !edit && (
          <button type="button" className="btn pri" onClick={() => setEdit('new')}>
            <Icon name="plus" />
            New tour
          </button>
        )}
      </div>
      {site.error ? <InlineError error={site.error} /> : null}
      {edit && (
        <TourForm
          key={edit === 'new' ? 'new' : edit.id}
          siteId={siteId}
          tour={edit === 'new' ? null : edit}
          cams={cams}
          onCancel={() => setEdit(null)}
          onDone={(t) => {
            replace(t);
            setEdit(null);
          }}
        />
      )}
      {confirm && (
        <div className="confirmbar danger" role="group" aria-label="Switch the tour off">
          <span>
            Switch off <b>{confirm.name}</b>? It stops running; its runs stay on record.
          </span>
          <button type="button" className="btn danger" onClick={() => void setActive(confirm, false)} disabled={busy === confirm.id}>
            Switch off
          </button>
          <button type="button" className="btn" onClick={() => setConfirm(null)}>
            Cancel
          </button>
        </div>
      )}
      <InlineError error={err} />
      {list.length === 0 ? (
        <Empty>No guard tours at this site.{canEdit ? ' Add one to have the cameras checked on a schedule.' : ''}</Empty>
      ) : (
        <div className="card2 flush">
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th scope="col">Tour</th>
                  <th scope="col">Cameras</th>
                  <th scope="col">Schedule</th>
                  <th scope="col">Looks for</th>
                  <th scope="col">Last run</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {list.map((t) => {
                  const lr = t.lastRun;
                  return (
                    <tr key={t.id} className={t.active ? '' : 'dimrow'}>
                      <td>
                        <b>{t.name}</b>
                        <div>{t.active ? <Pill tone="ok">active</Pill> : <Pill>off</Pill>}</div>
                      </td>
                      <td className="note">{camNames(t.cameraIds, cams)}</td>
                      <td className="note">{scheduleText(t)}</td>
                      <td className="note">
                        {t.focus.map((f) => (f === 'person' ? 'people' : 'vehicles')).join(', ')} ≥ {pct(t.minConfidence)}
                      </td>
                      <td className="nowrap">
                        {lr ? (
                          <>
                            <span title={fmtDateTime(lr.startedAt, tz)}>{relAge(lr.startedAt)}</span> <StatusPill status={lr.status} />
                            <div className="note">
                              {lr.camerasChecked ?? 0} checked{lr.camerasUnavailable ? ` · ${lr.camerasUnavailable} unavailable` : ''} ·{' '}
                              <span className={lr.exceptions?.length ? 'ai-t' : ''}>{lr.exceptions?.length ?? 0} found</span>
                            </div>
                          </>
                        ) : (
                          <span className="dim">never</span>
                        )}
                      </td>
                      <td className="nowrap">
                        <span className="btns">
                          <button type="button" className={`btn sm${runsOf?.id === t.id ? ' on' : ''}`} onClick={() => setRunsOf((r) => (r?.id === t.id ? null : t))}>
                            <Icon name="log" />
                            Runs
                          </button>
                          {canEdit && (
                            <button type="button" className="btn sm" onClick={() => setEdit(t)} disabled={!!edit}>
                              Edit
                            </button>
                          )}
                          {canEdit &&
                            (t.active ? (
                              <button type="button" className="btn sm" onClick={() => setConfirm(t)} disabled={busy === t.id}>
                                Switch off
                              </button>
                            ) : (
                              <button type="button" className="btn sm" onClick={() => void setActive(t, true)} disabled={busy === t.id}>
                                Switch on
                              </button>
                            ))}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {runsOf && <Runs key={runsOf.id} siteId={siteId} tour={runsOf} cams={cams} tz={tz} onClose={() => setRunsOf(null)} />}
      {!canEdit && (
        <div className="note">
          <Icon name="lock" /> Read-only: only admins can change guard tours.
        </div>
      )}
    </div>
  );
}
